/* Copyright (c) 2020-present CloudIO Inc */

import {
  useStateXAtom,
  useStateXAtomSetter,
  useStateXGetter,
  useStateXSetter,
} from '@cloudio-saas/statex';
import { alpha, Box, darken, lighten, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { SxProps } from '@mui/system';
import cx from 'clsx';
import { useStaticRuntime } from 'common/Context';
import { HeaderRowsContext, useFocusCell } from 'components/DataGrid/Context';
import {
  useAttributeDirty,
  useCurrentDataStore,
  useIsEditable,
  useIsRecordAtIndexCurrent,
  useSetCurrentRecord,
} from 'components/DataStoreReference/DataStoreReference';
import zIndex from 'constants/ZIndex';
import { useDeveloperMode } from 'designer/atoms';
import Overlay from 'designer/Overlay';
import useDesignerLite from 'designer/useDesignerLite';
import {
  dataGridCopyData,
  dataGridSelectedPath,
  getDataStoreAlias,
  runtimeContextMenuAtom,
} from 'layout/Sidebar/atoms';
import {
  usePending,
  usePlatform,
  useStringProperty,
} from 'platform/PlatformHooks';
import React, {
  startTransition,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import SimpleLoading from 'root/SimpleLoading';
import { isDeveloper } from 'root/useUser';
import useTrigger from 'triggers/useTrigger';
import WaveDots from '../../../utils/WaveDots';
import {
  InternalCellRendererProps,
  isBodyRegion,
  isFooterRegion,
  isHeaderRegion,
} from './types';
import { focusItemIdAtom } from 'hooks/useFocus';
import { AnyColumn } from 'componentregistry';
import { SwitchColumnCaseItem } from 'componentregistry/components/DataGrid/columns/SwitchColumn/SwitchColumnCase';
import { ColumnFooterItem } from 'componentregistry/components/DataGrid/ColumnFooter/ColumnFooter';
import { useVisibleChildItems } from 'registry/Components';

const emptyDOMProps = {};

const useStyles = makeStyles((theme: Theme) => ({
  gridCell: {
    position: 'absolute',
    border: '1px solid transparent',
    padding: '2px 4px',
    boxSizing: 'border-box',
    // transitionProperty: 'width',
    // transitionDuration: '0.2s',
    // transitionTimingFunction: 'ease',
    outline: 'none',
    '&$focused': {
      zIndex: zIndex.GridHeader,
    },
    display: 'flex',
    alignItems: 'center',
  },
  overflowVisible: {
    overflow: 'visible',
  },
  rowLines: {
    borderBottomColor:
      theme.palette.mode === 'light'
        ? lighten(alpha(theme.palette.divider, 1), 0.88)
        : darken(alpha(theme.palette.divider, 1), 0.68),
  },
  columnLines: {
    borderLeftColor:
      theme.palette.mode === 'light'
        ? lighten(alpha(theme.palette.divider, 1), 0.88)
        : darken(alpha(theme.palette.divider, 1), 0.68),
  },
  groupRowLines: {
    borderBottomColor:
      theme.palette.mode === 'light'
        ? lighten(alpha(theme.palette.divider, 1), 0.75)
        : lighten(alpha(theme.palette.divider, 1), 0.75),
  },
  groupColumnLines: {
    borderLeftColor:
      theme.palette.mode === 'light'
        ? lighten(alpha(theme.palette.divider, 1), 0.75)
        : lighten(alpha(theme.palette.divider, 1), 0.75),
  },
  lastFixedLeftColumn: {
    borderRightColor:
      theme.palette.mode === 'light'
        ? lighten(alpha(theme.palette.divider, 1), 0.88)
        : darken(alpha(theme.palette.divider, 1), 0.68),
  },
  editable: {
    cursor: 'text',
  },
  highlightCell: {
    '&$focused': {
      backgroundColor: theme.palette.background.paper,
      borderColor: theme.palette.secondary.main,
      '&$readonly': {
        borderColor: theme.palette.divider,
        backgroundColor: theme.palette.background.default,
        cursor: 'default',
      },
    },
    '&$editing': {
      backgroundColor: theme.palette.background.paper,
      borderColor: theme.palette.primary.main,
      '&$readonly': {
        borderColor: theme.palette.divider,
      },
      '&$error': {
        borderColor: theme.palette.error.dark,
      },
    },
  },
  activeRowInd: {
    borderLeft: `4px solid ${theme.palette.primary.dark}`,
    paddingLeft: 1,
  },
  focused: {},
  readonly: {
    // backgroundColor: theme.palette.background.default,
  },
  editing: {},
  dirty: {
    '&::before': {
      borderColor: `${theme.palette.error.main} transparent transparent ${theme.palette.error.main}`,
      borderStyle: 'solid',
      borderWidth: '3px',
      content: '""',
      height: 0,
      left: 0,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 0,
      verticalAlign: 'top',
      width: 0,
      zIndex: 1,
    },
  },
  center: {
    textAlign: 'center',
    alignContent: 'center',
    justifyContent: 'center',
  },
  start: {
    textAlign: 'start',
  },
  end: {
    textAlign: 'end',
  },
  error: { borderColor: theme.palette.error.dark },
  transparent: {
    backgroundColor: 'transparent',
  },
  ioExcelDragger: {
    backgroundColor: 'rgb(66, 133, 244)',
    height: '6px',
    width: '6px',
    borderWidth: '0px',
    display: 'block',
    position: 'absolute',
    fontSize: '0',
    cursor: 'crosshair',
    bottom: '0',
    right: '0',
    padding: '2px 2px 2px 2px',
  },
  focusedCell: {
    borderColor: 'rgb(66, 133, 244)',
    border: '1.5px solid',
  },
  selected: {
    backgroundColor: '#f5fbff',
    borderBottom: '2px solid #ffffff',
    borderLeft: '2px solid #ffffff',
  },
  selectedTopEdge: {
    borderTop: '1px solid #0090d9',
  },
  selectedRightEdge: {
    borderRight: '1px solid #0090d9',
  },
  selectedLeftEdge: {
    borderLeft: '1px solid #0090d9',
  },
  selectedBottomEdge: {
    borderBottom: '1px solid #0090d9',
  },
}));

export default function Cell(
  props: InternalCellRendererProps,
): React.ReactElement | null {
  const {
    CellComponent,
    appUid,
    roleUid,
    className,
    columnIndex,
    columns,
    custom = false,
    dataContext,
    height,
    highlightCell = false,
    onClick,
    onKeyDown,
    onMouseKeyUp,
    onMouseKeyDown,
    pageId,
    region,
    resetScroll,
    rowIndex,
    showColumnLines,
    showRowLines,
    width,
    columnData,
    selectedLeftEdge,
    selectedRightEdge,
    selectedTopEdge,
    selectedBottomEdge,
    maxRow,
    maxColumn,
    onMouseKeyEnter,
    onMouseKeyLeave,
    currentCellUpdatable,
    enableSpreadSheetCopy,
    totalRows,
  } = props;

  let { style } = props;

  const classes = useStyles();
  const cellRef = useRef<HTMLDivElement>(null);
  const devProps = useDesignerLite(appUid, pageId, columns[columnIndex].itemId);
  const staticRuntime = useStaticRuntime();
  const devMode = useDeveloperMode(appUid, pageId) && !staticRuntime;
  const platform = usePlatform();
  const footerRegion = isFooterRegion(region);
  const bodyRegion = isBodyRegion(region);
  const headerRegion = isHeaderRegion(region);
  const setdataGridSelectedPath = useStateXAtomSetter(dataGridSelectedPath);
  const store = useCurrentDataStore();

  const onKeyDownInside = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (onKeyDown) {
      onKeyDown(event);
    }
  };

  // let the individual columns editors handle the focus
  // useEffect(() => {
  //   const ele = cellRef.current;
  //   if (ele && focused) {
  //     const timeout = setTimeout(() => {
  //       // wait for grid body to scroll before focusing
  //       // as focusing on an hidden field is causing the scrollbar to scroll 50%
  //       ele.focus();
  //     }, 0);
  //     return () => clearTimeout(timeout);
  //   }
  // }, [columnIndex, focused, rowIndex]);

  // useWhyDidYouUpdate(`${rowIndex}x${columnIndex}`, { ...props });

  const headerRows = useContext(HeaderRowsContext);

  const column = columns[columnIndex];

  const { viewAttribute, cssClassName } = column.props;
  let { insertable, updatable } = column.props;
  if (!columnData?.gridInsertable) {
    insertable = { type: 'boolean', value: false };
  }
  if (!columnData?.gridUpdatable) {
    updatable = { type: 'boolean', value: false };
  }
  const recordId =
    !custom && rowIndex - headerRows >= 0 && !footerRegion
      ? store?.getRecordIdAtRow(rowIndex - headerRows)
      : undefined;
  let errorMessage;
  let dirtyColumnItemId = column.itemId;
  if (column.type === 'SwitchColumn') {
    column.children.forEach((id) => {
      const switchCaseColumns = platform.getChildrenItemsById(
        appUid,
        pageId,
        id,
        (child): boolean => true,
      ) as AnyColumn[];
      const columnCaseMd = switchCaseColumns[0];
      if (columnCaseMd) {
        const { viewAttribute: switchViewAttribute } = columnCaseMd?.props;
        const msg =
          recordId &&
          switchViewAttribute &&
          store?.getError(switchViewAttribute, recordId);
        if (msg) {
          errorMessage = msg;
        }
      }
    });
    if (store && rowIndex - headerRows >= 0 && recordId) {
      const { expression } = column.props;
      const expResult = expression
        ? platform.evalIfExpressionString(
            appUid,
            pageId,
            column.itemId,
            'expression',
            { recordId: recordId, dsAlias: store.alias },
            expression,
          )
        : '';
      const switchCases = platform.getChildrenItemsById(
        appUid,
        pageId,
        column.itemId,
        (child) => {
          const caseValue = (child as SwitchColumnCaseItem).props.case;
          const matchedChild =
            child.type === 'SwitchColumnCase' &&
            (!caseValue || caseValue === expResult);

          if (matchedChild && (child.props.visible ?? true)) {
            if (!child.props.visibleCondition) {
              return true;
            } else {
              return platform.evalBoolean(
                appUid,
                pageId,
                child.itemId,
                'visibleCondition',
                { rowIndex, dsAlias: store.alias },
                child.props.visibleCondition,
              );
            }
          }
          return false;
        },
      ) as SwitchColumnCaseItem[];
      const columnCaseMd = switchCases[0];
      if (columnCaseMd) {
        const c = platform.getItem(
          appUid,
          pageId,
          columnCaseMd.children[0],
        ) as AnyColumn;
        dirtyColumnItemId = c.itemId;
      }
    }
  } else {
    errorMessage =
      viewAttribute && recordId && store?.getError(viewAttribute, recordId);
  }

  const dirty = useAttributeDirty(
    useStringProperty({
      appUid,
      itemId: dirtyColumnItemId,
      pageId,
      prop: 'viewAttribute',
    }),
    bodyRegion ? rowIndex - headerRows : Number.MAX_VALUE,
  );

  const editable = useIsEditable({
    appUid,
    pageId,
    itemId: column.itemId,
    insertable: insertable || { type: 'boolean', value: false },
    updatable: updatable || { type: 'boolean', value: false },
    rowIndex: bodyRegion ? rowIndex - headerRows : Number.MAX_VALUE,
  });
  const activeRecord =
    useIsRecordAtIndexCurrent(
      bodyRegion ? rowIndex - headerRows : Number.MAX_VALUE,
    ) && !custom;
  // useWhyDidYouUpdate('Cell', {
  //   ...props,
  //   editable,
  //   dirty,
  //   headerRows,
  //   hoverRowIndex,
  // });

  const [onCellClickTrigger] = useTrigger(
    {
      platform,
      appUid,
      roleUid,
      pageId,
      itemId: column.itemId,
      disableProgressTracking: true,
    },
    'TriggerOnCellClick',
  );
  const [pending, setPending] = usePending();
  const setCurrentRecord = useSetCurrentRecord();
  const set = useStateXSetter();

  const dsAlias = (getDataStoreAlias(column) || store?.alias) ?? '';
  const openRecordingMenu = platform.isRecording() && bodyRegion;
  const itemId = column.itemId;
  const _cssClassName = cssClassName
    ? platform.evalIfExpressionString(
        appUid,
        pageId,
        column.itemId,
        'cssClassName',
        { recordId: recordId, dsAlias: store?.alias },
        cssClassName,
      )
    : '';
  const rowBGString = dataContext.rowBackgroundColor?.value
    ? platform.evalIfExpressionString(
        appUid,
        pageId,
        column.itemId,
        'rowBackgroundColor',
        { recordId: recordId, dsAlias: store?.alias },
        dataContext.rowBackgroundColor,
      )
    : null;

  let _cssFooterClassName;
  const _items = useVisibleChildItems(appUid, pageId, column.itemId);

  if (footerRegion) {
    const footers = _items.filter(
      (child) => child?.type === 'ColumnFooter',
    ) as ColumnFooterItem[];

    const footerIndex = rowIndex - (totalRows - (dataContext.footerRows || 0));
    const footer = footers[footerIndex];
    if (footer) {
      const { cssClassName: cssFooterClassName } = footer.props;
      _cssFooterClassName = cssFooterClassName
        ? platform.evalIfExpressionString(
            appUid,
            pageId,
            footer.itemId,
            'cssClassName',
            {},
            cssFooterClassName,
          )
        : '';
    }
  }
  const domProps = useMemo(() => {
    if (((headerRegion && rowIndex === 0) || openRecordingMenu) && !devMode) {
      const developer = isDeveloper(platform, appUid);
      return developer
        ? {
            onContextMenu: staticRuntime
              ? undefined
              : (e: React.MouseEvent<HTMLElement>) => {
                  e.stopPropagation();
                  e.preventDefault();
                  set(runtimeContextMenuAtom, {
                    pageId,
                    itemId,
                    left: e.clientX,
                    top: e.clientY,
                    orphan: false,
                    dsAlias,
                    rowIndex: rowIndex ?? -1,
                  });
                },
          }
        : {};
    }
    return emptyDOMProps;
  }, [
    headerRegion,
    rowIndex,
    openRecordingMenu,
    devMode,
    platform,
    appUid,
    staticRuntime,
    set,
    pageId,
    itemId,
    dsAlias,
  ]);
  const focusSignal = useFocusCell();
  const [focused, setFocused] = useState(false);
  const [editing, setEditing] = useState(false);
  const [focusItemId, setFocusItemId] = useStateXAtom(focusItemIdAtom);
  const get = useStateXGetter();

  useLayoutEffect(() => {
    {
      const { focusColumn, focusRow, editing: _editing } = focusSignal.peek();
      if (focusColumn === columnIndex && focusRow === rowIndex) {
        setFocused(true);
        setEditing(_editing);
      } else {
        setFocused(false);
        setEditing(false);
      }
    }
    if (focusItemId && focusItemId === itemId && activeRecord) {
      focusSignal.set((v) => ({
        editing: true,
        focusColumn: columnIndex,
        focusRow: rowIndex,
      }));
      setFocusItemId('');
    }
    return focusSignal.onChange(
      ({ focusColumn, focusRow, editing: _editing }) => {
        if (focusColumn === columnIndex && focusRow === rowIndex) {
          setFocused(true);
          setEditing(_editing);
        } else {
          setFocused(false);
          setEditing(false);
        }
      },
    );
  }, [
    rowIndex,
    columnIndex,
    focusSignal,
    focusItemId,
    itemId,
    dataContext.headerRows,
    dataContext.groupHeadersRows,
    activeRecord,
  ]);

  // console.log('Cell.render', rowIndex, columnIndex);
  // useWhyDidYouUpdate('Cell', { ...props, editable, dirty, headerRows });

  const ignoreColorProps =
    column.type === 'SelectColumn' && column.props.renderAsPill;

  if (Number.isNaN(width)) {
    // console.error('missing width!', { columnIndex, width, rowIndex, region });
    return null;
  }

  if (custom) {
    return (
      <CellComponent
        active={focused}
        appUid={appUid}
        roleUid={roleUid}
        column={columns[columnIndex]}
        columnIndex={columnIndex}
        dataContext={dataContext}
        devProps={devProps}
        domProps={domProps}
        editable={editable}
        editing={editable && editing}
        height={height}
        pageId={pageId}
        region={region}
        resetScroll={resetScroll}
        rowIndex={rowIndex}
        style={style}
        width={width}
        platform={platform}
        columnData={columnData}
      />
    );
  }

  // function handleMouseOver() {
  //   if (cellRef.current) {
  //     setHoverRowIndex(rowIndex);
  //     dispatch({
  //       active: true,
  //       columnIndex,
  //       left: cellRef.current.offsetLeft + cellRef.current.clientWidth,
  //     });
  //   }
  // }

  // function onMouseOver() {
  //   setHoverRowIndex(rowIndex);
  // }
  function onMouseEnter(e: React.MouseEvent<HTMLDivElement>) {
    if (onMouseKeyEnter) {
      e.stopPropagation();
      onMouseKeyEnter(e);
    }
    const el = cellRef.current;
    if (el) {
      if (devProps.onMouseEnter) {
        devProps.onMouseEnter(e);
      }
    }
  }
  function onMouseLeave(e: React.MouseEvent<HTMLDivElement>) {
    if (onMouseKeyLeave) {
      e.stopPropagation();
      onMouseKeyLeave(e);
    }
    if (cellRef.current) {
      if (devProps.onMouseLeave) {
        devProps.onMouseLeave(e);
      }
    }
  }
  let isTextSelectable: boolean;
  const dataGridItem = platform.findParentOfType(
    appUid,
    pageId,
    column.itemId,
    'DataGrid',
  );
  if (dataGridItem) {
    const childrenItems = platform.getChildrenItemsById(
      appUid,
      pageId,
      dataGridItem.itemId,
      (child) => {
        return child.type === 'TriggerOnRowDoubleClick';
      },
    );
    isTextSelectable = childrenItems.length === 0;
  }
  function onMouseDown(e: React.MouseEvent<HTMLDivElement>) {
    if (onMouseKeyDown) {
      if (e.target instanceof HTMLElement) {
        // Adjust text selection based on if the click is more than once and the isTextSelectable variable
        if (!isTextSelectable && e.detail > 1) {
          e.target.style.userSelect = 'none';
        } else {
          e.target.style.userSelect = 'text';
        }
      }
      e.stopPropagation();
      onMouseKeyDown(e);
    }
  }

  function onMouseUp(e: React.MouseEvent<HTMLDivElement>) {
    const copyData = get(dataGridCopyData);
    const eventType = copyData?.eventType;
    if (onMouseKeyUp) {
      onMouseKeyUp(e, eventType);
    }
  }

  const onExcelMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    const eventType = 'EXCEL';
    if (onMouseKeyDown) {
      event.stopPropagation();
      onMouseKeyDown(event, eventType);
    }
  };

  const onExcelMouseUp = (event: React.MouseEvent<HTMLDivElement>) => {
    const eventType = 'EXCEL';
    if (onMouseKeyUp) {
      event.stopPropagation();
      onMouseKeyUp(event, eventType);
    }
  };
  // const column = columns[columnIndex];
  const tip = errorMessage
    ? {
        'data-tip': errorMessage,
        'data-tip-at': 'top',
        'data-tip-error': true,
        'data-tip-delay': 0,
      }
    : undefined;

  if (devMode && columns[columnIndex].type === 'DivColumn') {
    style = { ...style, zIndex: 1 };
  }

  const { color, bgColor } = ignoreColorProps
    ? { color: undefined, bgColor: undefined }
    : columnData ?? {};

  let sx: SxProps<Theme> = {};
  let hasColor = false;
  if (bodyRegion && (color || bgColor || rowBGString) && !editing) {
    hasColor = true;
    sx = {
      bgcolor: bgColor || rowBGString,
      color,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    };
  }

  const groupHeader =
    dataContext?.groupHeadersRows &&
    dataContext?.groupHeadersRows - rowIndex > 0;

  if (headerRegion && dataContext.headerBackgroundColor !== 'transparent') {
    sx = {
      ...sx,
      backgroundColor: groupHeader
        ? undefined
        : dataContext.headerBackgroundColor,
      color: groupHeader ? undefined : dataContext.headerColor,
      '& input': {
        color: dataContext.headerColor,
      },
      '& svg': {
        fill: dataContext.headerColor,
        stroke: dataContext.headerColor,
      },
    };
  }
  const _className = bodyRegion
    ? _cssClassName
    : footerRegion
    ? _cssFooterClassName
    : '';
  if (groupHeader) {
    sx = {
      ...sx,
      backgroundColor: 'rgb(0, 0, 0, 0.08)',
    };
  }
  const menuProps = openRecordingMenu ? domProps : {};
  return (
    <Box
      sx={sx}
      // onMouseOverCapture={header ? handleMouseOver : undefined}
      // onMouseLeave={() => dispatch({ active: false, columnIndex: -1 })}
      onKeyDown={onKeyDownInside}
      onFocusCapture={(event: React.FocusEvent<HTMLElement>) => {
        if (editable) {
          focusSignal.set((v) => ({
            ...v,
            editing: true,
            focusColumn: columnIndex,
            focusRow: rowIndex,
          }));
        }
      }}
      onFocus={() => {
        if (editable) {
          focusSignal.set((v) => ({
            ...v,
            editing: true,
            focusColumn: columnIndex,
            focusRow: rowIndex,
          }));
        }
      }}
      onBlur={() => {
        if (editable && !focusSignal.get().lov) {
          focusSignal.set((v) => ({
            ...v,
            editing: false,
          }));
        }
      }}
      ref={cellRef}
      role="gridcell"
      aria-colindex={columnIndex}
      aria-rowindex={rowIndex}
      data-testid={
        headerRegion
          ? rowIndex === 0
            ? `${itemId}-header`
            : `${itemId}-filter`
          : bodyRegion
          ? itemId
          : undefined
      }
      className={cx(
        'io-cell',
        classes.gridCell,
        className,
        _className,
        columnIndex % 2 === 0 ? 'io-grid-col-even' : 'io-grid-col-odd',
        {
          [classes.focused]: focused,
          [classes.columnLines]:
            !groupHeader && showColumnLines && columnIndex > 0,
          [classes.groupColumnLines]:
            groupHeader && showColumnLines && columnIndex > 0,
          // [classes.lastFixedLeftColumn]:
          //   lastColumn &&
          //   (region === 'body-left' ||
          //     region === 'footer-left' ||
          //     region === 'header-left'),
          [classes.lastFixedLeftColumn]:
            columnIndex === dataContext.totalColumns - 1,
          [classes.rowLines]: !groupHeader && showRowLines,
          [classes.groupRowLines]: groupHeader,
          [classes.editing]: editing && editable && bodyRegion,
          [classes.dirty]: dirty,
          dirty: dirty,
          [classes.editable]: editable,
          [classes.readonly]: !editable,
          [classes.activeRowInd]:
            activeRecord && columnIndex === 0 && dataContext.highlightActiveRow,
          [classes.transparent]:
            headerRegion && dataContext.headerBackgroundColor !== 'transparent'
              ? false
              : dataContext.transparentBG,
          [classes.highlightCell]: !hasColor && highlightCell,
          [classes.center]: column.props.alignment === 'center',
          [classes.start]: column.props.alignment === 'start',
          [classes.end]: column.props.alignment === 'end',
          [classes.error]: !!errorMessage,
          [classes.overflowVisible]: column.type === 'IconColumn',
          [classes.selectedBottomEdge]: selectedBottomEdge,
          [classes.selectedLeftEdge]: selectedLeftEdge,
          [classes.selectedTopEdge]: selectedTopEdge,
          [classes.selectedRightEdge]: selectedRightEdge,
        },
      )}
      style={style}
      tabIndex={-1}
      {...devProps}
      {...menuProps}
      {...tip}
      // onMouseOver={onMouseOver}
      onMouseEnter={custom ? undefined : onMouseEnter}
      onMouseLeave={custom ? undefined : onMouseLeave}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      onClick={async (e) => {
        if (
          [
            'FormulaStringColumn',
            'FormulaNumberColumn',
            'AttachmentColumn',
          ].includes(column.type)
        ) {
          return;
        }
        if (bodyRegion && !pending) {
          platform.startAsyncTransition(
            async () => {
              setCurrentRecord(rowIndex - headerRows);
              if (onCellClickTrigger) {
                e.stopPropagation();
                await onCellClickTrigger(e);
              }
              setdataGridSelectedPath({
                dragFromCell: {
                  rowIndex: rowIndex,
                  columnIndex: columnIndex,
                },
                dragToCell: {
                  rowIndex: rowIndex,
                  columnIndex: columnIndex,
                },
                dragging: false,
              });
              onClick?.(e, { columnIndex, column, rowIndex });
              if (editable) {
                startTransition(() => {
                  focusSignal.set({
                    editing: true,
                    focusColumn: columnIndex,
                    focusRow: rowIndex,
                  });
                });
              }
            },
            setPending,
            { itemId, pageId, appUid },
          );
        }
        if (devProps.onClick) {
          devProps.onClick(e);
        }
      }}
    >
      <React.Suspense fallback={<SimpleLoading name="Cell" />}>
        <CellComponent
          active={focused}
          appUid={appUid}
          roleUid={roleUid}
          column={columns[columnIndex]}
          columnIndex={columnIndex}
          dataContext={dataContext}
          devProps={devProps}
          domProps={domProps}
          editable={editable}
          editing={editable && editing}
          height={height}
          pageId={pageId}
          region={region}
          resetScroll={resetScroll}
          rowIndex={rowIndex}
          width={width}
          platform={platform}
          columnData={columnData}
        />
        {columnIndex === maxColumn &&
        rowIndex === maxRow &&
        rowIndex > (dataContext?.groupHeadersRows ?? 0) &&
        currentCellUpdatable &&
        enableSpreadSheetCopy ? (
          <div
            aria-hidden="true"
            className={classes.ioExcelDragger}
            onMouseDown={onExcelMouseDown}
            onMouseUp={onExcelMouseUp}
          ></div>
        ) : (
          ''
        )}
      </React.Suspense>
      {pending && <WaveDots active />}
      {devMode &&
      columns[columnIndex].type !== 'SwitchColumn' &&
      columns[columnIndex].type !== 'DivColumn' &&
      isBodyRegion(region) ? (
        <Overlay {...devProps} {...domProps} />
      ) : null}
    </Box>
  );
}
